import type { AccountChangeCategory, AccountChangeEntry } from '../api/client'

export type ChangeFilter = 'all' | AccountChangeCategory

export const CATEGORY_FILTERS: { key: ChangeFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'profile', label: 'Profile' },
  { key: 'address', label: 'Address' },
  { key: 'billing', label: 'Billing & card' },
  { key: 'auto_renew', label: 'Auto-renew' },
  { key: 'status', label: 'Status' },
]

export function filterChanges(rows: AccountChangeEntry[], filter: ChangeFilter): AccountChangeEntry[] {
  return filter === 'all' ? rows : rows.filter((r) => r.category === filter)
}

const SWITCHES = new Set(['auto_renew', 'do_not_renew', 'never_expires', 'print_standing'])

// Postgres timestamptz text in a UTC session: "2026-09-30 21:50:28.123+00",
// with 0-6 fractional digits.
const PG_TS = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})(?::\d{2}(?:\.\d{1,6})?)?\+00$/

export function formatChangeValue(field: string, value: string | null): string {
  if (value === null || value === '') return '—'
  if (SWITCHES.has(field)) return value === 'true' ? 'On' : value === 'false' ? 'Off' : value
  if (field === 'auto_renew_price') return `$${value}`
  const ts = PG_TS.exec(value)
  if (ts) return `${ts[1]} ${ts[2]} UTC`
  return value
}

export type DetailLine = { label: string; value: string }

// Field labels for before/after pairs, matching the backend's FIELDS labels.
const FIELD_LABELS: Record<string, string> = {
  current_period_end: 'Paid through',
  expired_at: 'Expires',
  auto_renew: 'Auto-renew',
  do_not_renew: 'Do not renew',
  status: 'Status',
  billing_plan: 'Plan',
  payment_instrument: 'Payment method',
  subscription_source: 'Billing source',
  auto_renew_price: 'Renewal price',
  gateway_profile_id: 'Gateway customer profile',
  gateway_payment_profile_id: 'Gateway payment profile',
  gateway_customer_id: 'Gateway customer id',
  paypal_subscription_id: 'PayPal agreement',
  issues_remaining: 'Issues remaining',
}

const KEY_LABELS: Record<string, string> = {
  provider_refund_id: 'Refund transaction',
  refund_id: 'Refund #',
  order_id: 'Order #',
  payer_subscriber_id: 'Paid by account',
  member_subscriber_id: 'Member account',
  previous_payer: 'Previous payer account',
  previous_period_end: 'Previous paid-through',
  access_until: 'Access until',
  stopped_at: 'Stopped at',
  woo_site: 'Store site',
}

const PROVIDERS: Record<string, string> = { authnet: 'Authorize.Net', paypal: 'PayPal', stripe: 'Stripe' }

// Shown elsewhere on the row (the title badge) — repeating them is noise.
const SKIP_KEYS = new Set(['publication_slug', 'before', 'after'])

const CARD_KEYS = ['card_type', 'last4', 'card_expiry']

function humanise(key: string): string {
  const words = key.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function plain(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (key === 'provider' && typeof value === 'string') return PROVIDERS[value] ?? value
  if (key === 'amount') return `$${value}`
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${plain(k, v)}`)
      .join(', ')
  }
  return String(value)
}

function card(side: Record<string, unknown>): string {
  const [type, last4, exp] = CARD_KEYS.map((k) => side[k])
  const parts = [type, last4 ? `····${last4}` : null, exp ? `exp ${exp}` : null].filter(Boolean)
  return parts.length ? parts.join(' ') : '—'
}

function pairValue(field: string, value: unknown): string {
  if (typeof value === 'boolean' && SWITCHES.has(field)) return value ? 'On' : 'Off'
  if (field === 'auto_renew_price' && value !== null && value !== undefined) return `$${value}`
  return plain(field, value)
}

/** audit_log details as readable "Label: value" lines, before/after first. */
export function formatActionDetails(details: Record<string, unknown>): DetailLine[] {
  const lines: DetailLine[] = []
  const before = (details.before ?? {}) as Record<string, unknown>
  const after = (details.after ?? {}) as Record<string, unknown>
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  if (keys.some((k) => CARD_KEYS.includes(k))) {
    lines.push({ label: 'Card on file', value: `${card(before)} → ${card(after)}` })
  }
  for (const k of keys) {
    if (CARD_KEYS.includes(k)) continue
    if (k in before && k in after && JSON.stringify(before[k]) === JSON.stringify(after[k])) continue
    const value = !(k in after)
      ? `was ${pairValue(k, before[k])}`
      : k in before
        ? `${pairValue(k, before[k])} → ${pairValue(k, after[k])}`
        : pairValue(k, after[k])
    lines.push({ label: FIELD_LABELS[k] ?? humanise(k), value })
  }
  for (const [k, v] of Object.entries(details)) {
    if (SKIP_KEYS.has(k) || v === null || v === undefined || v === '') continue
    lines.push({ label: KEY_LABELS[k] ?? humanise(k), value: plain(k, v) })
  }
  return lines
}
